import { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Card, Icon, IconButton, CircularProgress, Collapse, LinearProgress, Tab, Tabs } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { get, getFile } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import { useMaterialUIController } from "context";
import Painel from "./analiseTempo/Painel";
import KpiCard from "./analiseTempo/KpiCard";
import GraficoEvolucao from "./analiseTempo/GraficoEvolucao";
import GraficoDistribuicao from "./analiseTempo/GraficoDistribuicao";
import GraficoEsforco from "./analiseTempo/GraficoEsforco";
import MatrizEsforco from "./analiseTempo/MatrizEsforco";
import TabelaPercentual from "./analiseTempo/TabelaPercentual";
import {
    CLIENTES, calcularIndicadores, consolidarBlocos, coresPorPessoa, corMedida, formatarDecimal, formatarNumero,
    formatarPercentualCurto, numerosDaPessoa, trimestreAnterior, variacaoPercentual,
} from "./analiseTempo/analiseUtils";

const formatoVariacao = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: "always" });
const variacaoCard = (variacao) => (variacao === null || variacao === undefined || !Number.isFinite(variacao)
    ? null
    : { texto: `${formatoVariacao.format(variacao * 100)}%`, positiva: variacao >= 0 });
const variacaoPontos = (atual, anterior) => (atual === null || atual === undefined || anterior === null || anterior === undefined
    ? null
    : { texto: `${formatoVariacao.format((atual - anterior) * 100)} p.p.`, positiva: atual >= anterior });

// Mesmas abas de análise da planilha exportada
const VISOES = [
    {
        value: "negociadores",
        label: "Análise Negociadores",
        rotuloLinhas: "Cliente/Negociador",
        rotuloPessoa: "Negociador",
        rotuloPessoas: "negociadores",
        ajuda: "Percentual das fichas acionadas pelo negociador em cada cliente, mês a mês (cada mês soma 100%). Entram negociadores e backoffice, inclusive inativos.",
    },
    {
        value: "coordenadores",
        label: "Análise Supervisão (Coordenador)",
        rotuloLinhas: "Cliente/Coordenador",
        rotuloPessoa: "Coordenador",
        rotuloPessoas: "coordenadores",
        ajuda: "O coordenador não é contado pelos próprios acionamentos: cada ficha acionada pelos negociadores conta como apoio do coordenador da equipe da carteira dessa ficha.",
    },
];

const OPCAO_TODOS = { id: "__todos__", value: "__todos__", label: "Todos" };
const OPCAO_TODAS_CARTEIRAS = { id: "__todas__", value: "", label: "Todas" };
const OPCAO_TODOS_CLIENTES = { id: "__todos_clientes__", value: "", label: "Todos" };
const CLIENTES_OPTIONS = [OPCAO_TODOS_CLIENTES, ...CLIENTES.map(c => ({ id: c, value: c, label: c }))];

const rotuloFiltro = { px: 1, pb: 0.5, display: "block" };

const TRIMESTRES_OPTIONS = [
    { id: 1, value: 1, label: "1º Trimestre (Jan a Mar)" },
    { id: 2, value: 2, label: "2º Trimestre (Abr a Jun)" },
    { id: 3, value: 3, label: "3º Trimestre (Jul a Set)" },
    { id: 4, value: 4, label: "4º Trimestre (Out a Dez)" },
];

export default function AnaliseTempoSemestral() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const currentYear = new Date().getFullYear();
    const [ano, setAno] = useState({ id: currentYear, value: currentYear, label: String(currentYear) });
    const [trimestre, setTrimestre] = useState(TRIMESTRES_OPTIONS[Math.floor(new Date().getMonth() / 3)]);
    const [abaAtual, setAbaAtual] = useState(0);
    const visao = VISOES[abaAtual];
    const [pessoa, setPessoa] = useState(OPCAO_TODOS);
    const [cliente, setCliente] = useState(OPCAO_TODOS_CLIENTES);
    const [carteirasDisponiveis, setCarteirasDisponiveis] = useState([]);
    const [restritoEquipe, setRestritoEquipe] = useState(false);
    const [carteira, setCarteira] = useState(OPCAO_TODAS_CARTEIRAS);
    const [dados, setDados] = useState(null);
    const [dadosAnterior, setDadosAnterior] = useState(null);
    const [loadingConsulta, setLoadingConsulta] = useState(false);
    const [loading, setLoading] = useState(false);
    const [detalheAberto, setDetalheAberto] = useState(true);
    const requisicaoAtual = useRef(0);
    const cacheConsultas = useRef(new Map());
    const detalheRef = useRef(null);

    const anosOptions = [0, 1, 2, 3].map(i => ({ id: currentYear - i, value: currentYear - i, label: String(currentYear - i) }));

    useEffect(() => {
        get("/relatorios/analise-tempo/carteiras")
            .then(res => {
                setCarteirasDisponiveis(Array.isArray(res?.carteiras) ? res.carteiras : []);
                setRestritoEquipe(!!res?.restritoEquipe);
            })
            .catch(err => {
                console.error("Erro ao carregar carteiras da análise:", err);
                setCarteirasDisponiveis([]);
            });
    }, []);

    const carteirasOptions = useMemo(
        () => [
            OPCAO_TODAS_CARTEIRAS,
            ...carteirasDisponiveis.map(c => ({ id: c.id, value: c.id, label: c.ativo ? c.nome : `${c.nome} (inativa)` })),
        ],
        [carteirasDisponiveis]
    );

    // Cliente e pessoa filtram só a tela; período e carteira vão para o backend (e para a exportação)
    const filtrosValidos = !!(ano?.value && trimestre?.value);
    const parametrosConsulta = [
        `ano=${ano?.value}`,
        `trimestre=${trimestre?.value}`,
        carteira?.value ? `carteiras=${carteira.value}` : null,
    ].filter(Boolean).join("&");

    // Mesmos filtros, trimestre anterior: só para as variações dos cards
    const anterior = trimestreAnterior(ano?.value, trimestre?.value);
    const parametrosAnterior = [
        `ano=${anterior.ano}`,
        `trimestre=${anterior.trimestre}`,
        carteira?.value ? `carteiras=${carteira.value}` : null,
    ].filter(Boolean).join("&");

    useEffect(() => {
        if (!filtrosValidos) return undefined;
        // Só a resposta da última troca de filtro vale; as anteriores são descartadas
        const idRequisicao = ++requisicaoAtual.current;
        const vigente = () => idRequisicao === requisicaoAtual.current;
        // A consulta é pesada: reaproveita períodos já carregados nesta tela
        const buscar = (parametros) => {
            const cache = cacheConsultas.current;
            if (cache.has(parametros)) return Promise.resolve(cache.get(parametros));
            return get(`/relatorios/analise-tempo?${parametros}`).then(res => {
                cache.set(parametros, res);
                return res;
            });
        };

        setLoadingConsulta(true);
        // Espera o usuário terminar de trocar os filtros antes de consultar, para não empilhar consultas no banco
        const espera = setTimeout(() => {
            buscar(parametrosConsulta)
                .then(res => {
                    if (!vigente()) return;
                    setDados(res);
                    setLoadingConsulta(false);
                    // Trimestre anterior (variações dos cards) só depois do atual, para não disputar o banco
                    setDadosAnterior(null);
                    buscar(parametrosAnterior)
                        .then(anteriorRes => {
                            if (vigente()) setDadosAnterior(anteriorRes);
                        })
                        .catch(err => console.error("Sem comparação com o trimestre anterior:", err));
                })
                .catch(err => {
                    if (!vigente()) return;
                    console.error(err);
                    setDados(null);
                    setLoadingConsulta(false);
                    errorAlert(err?.message || "Erro ao carregar a Análise de Atendimento.");
                });
        }, 400);
        return () => clearTimeout(espera);
    }, [parametrosConsulta, parametrosAnterior, filtrosValidos]);

    useEffect(() => {
        setPessoa(OPCAO_TODOS);
    }, [visao.value, dados]);

    const meses = dados?.meses || [];
    const qtdMeses = meses.length;
    const blocos = useMemo(() => (dados && dados[visao.value]) || [], [dados, visao.value]);
    const pessoasOptions = useMemo(
        () => [OPCAO_TODOS, ...blocos.map(b => ({ id: b.nome, value: b.nome, label: b.nome }))],
        [blocos]
    );
    const pessoaNome = pessoa?.value && pessoa.value !== OPCAO_TODOS.value ? pessoa.value : null;
    const clienteNome = cliente?.value || null;

    const indicadores = useMemo(
        () => calcularIndicadores(dados, blocos, pessoaNome, clienteNome),
        [dados, blocos, pessoaNome, clienteNome]
    );
    const consolidado = useMemo(
        () => consolidarBlocos(blocos, `Todos os ${visao.rotuloPessoas}`, qtdMeses),
        [blocos, visao.rotuloPessoas, qtdMeses]
    );
    const itensDistribuicao = useMemo(
        () => blocos.map(b => ({ nome: b.nome, valor: numerosDaPessoa(b, clienteNome, qtdMeses).atendimentos })),
        [blocos, clienteNome, qtdMeses]
    );

    // Mesmo escopo (pessoa e cliente) no trimestre anterior, para as variações dos cards
    const indicadoresAnterior = useMemo(() => {
        const blocosAnterior = (dadosAnterior && dadosAnterior[visao.value]) || [];
        return calcularIndicadores(dadosAnterior, blocosAnterior, pessoaNome, clienteNome);
    }, [dadosAnterior, visao.value, pessoaNome, clienteNome]);
    const rotuloVariacao = `vs ${anterior.trimestre}º tri/${anterior.ano}`;
    // Mesma cor por pessoa na rosca e no gráfico por cliente
    const cores = useMemo(() => coresPorPessoa(blocos, darkMode), [blocos, darkMode]);
    const fichasPorCliente = useMemo(() => {
        const mapa = {};
        (dados?.clientes || []).forEach(c => { mapa[c.cliente] = c.fichasPeriodo; });
        return mapa;
    }, [dados]);
    const clienteDestaque = indicadores?.principal?.cliente;
    const blocoDetalhe = pessoaNome ? blocos.find(b => b.nome === pessoaNome) || consolidado : consolidado;

    // Resumo por cliente do detalhamento: "Todos" usa os totais do backend (cada ficha uma vez)
    const resumoClientes = CLIENTES
        .map(nomeCliente => {
            const linha = blocoDetalhe.linhas.find(l => l.cliente === nomeCliente);
            if (!linha) return null;
            const total = !pessoaNome ? (dados?.clientes || []).find(c => c.cliente === nomeCliente) : null;
            return {
                cliente: nomeCliente,
                fichas: total ? total.fichasPeriodo : linha.fichasPeriodo,
                atendimentos: total ? total.atendimentosPeriodo : linha.atendimentosTotal,
                percentual: linha.percentualTotal,
            };
        })
        .filter(Boolean);

    const handleVerDetalhe = (nome) => {
        setPessoa({ id: nome, value: nome, label: nome });
        setDetalheAberto(true);
        setTimeout(() => detalheRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    };

    const handleExportar = () => {
        if (!filtrosValidos) {
            errorAlert("Por favor, selecione o Ano e o Trimestre.");
            return;
        }

        setLoading(true);
        const url = `/relatorios/analise-tempo/exportar?${parametrosConsulta}`;
        const filename = `Analise_${trimestre.value}Trimestre_${ano.value}.xlsx`;

        getFile(url, filename)
            .then(() => {
                successAlert("Relatório trimestral exportado com sucesso!");
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                errorAlert(err?.message || "Erro ao exportar a Análise de Atendimento.");
                setLoading(false);
            });
    };

    const semDados = !loadingConsulta && (!dados || blocos.length === 0);
    // Na troca de filtro os dados atuais continuam na tela (esmaecidos) até os novos chegarem
    const atualizando = loadingConsulta && !!dados;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card
                    variant="outlined"
                    sx={{
                        p: 3,
                        background: darkMode ? "#4B4F5C" : "#fafafa",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <MDBox sx={{ borderBottom: 1, width: "100%", borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "divider", mb: 3 }}>
                        <Tabs
                            value={abaAtual}
                            onChange={(event, novaAba) => setAbaAtual(novaAba)}
                            aria-label="abas da análise de atendimento"
                            textColor={darkMode ? "inherit" : "primary"}
                            indicatorColor="info"
                        >
                            {VISOES.map((v, idx) => (
                                <Tab
                                    key={v.value}
                                    label={v.label}
                                    sx={{ fontSize: 12, color: darkMode ? (abaAtual === idx ? "#fff" : "rgba(255, 255, 255, 0.7)") : "inherit" }}
                                />
                            ))}
                        </Tabs>
                    </MDBox>

                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={6} md={2}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Ano de Referência</MDTypography>
                            <MDSelect isMulti={false} name="ano" value={ano} options={anosOptions} onChange={val => setAno(val)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Trimestre</MDTypography>
                            <MDSelect isMulti={false} name="trimestre" value={trimestre} options={TRIMESTRES_OPTIONS} onChange={val => setTrimestre(val)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Carteira</MDTypography>
                            <MDSelect
                                isMulti={false}
                                name="carteira"
                                value={carteira}
                                options={carteirasOptions}
                                onChange={val => setCarteira(val || OPCAO_TODAS_CARTEIRAS)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Cliente</MDTypography>
                            <MDSelect
                                isMulti={false}
                                name="cliente"
                                value={cliente}
                                options={CLIENTES_OPTIONS}
                                onChange={val => setCliente(val || OPCAO_TODOS_CLIENTES)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>{visao.rotuloPessoa}</MDTypography>
                            <MDSelect
                                isMulti={false}
                                name="pessoa"
                                value={pessoa}
                                options={pessoasOptions}
                                isDisabled={loadingConsulta || blocos.length === 0}
                                onChange={val => setPessoa(val || OPCAO_TODOS)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "flex-end", pt: { sm: "38px !important" } }}>
                            <MDButton variant="gradient" color="info" onClick={handleExportar} disabled={loading} fullWidth sx={{ height: 40, px: 2 }}>
                                {loading ? (
                                    <>
                                        <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                                        Gerando...
                                    </>
                                ) : (
                                    <>
                                        <Icon sx={{ mr: 1 }}>file_download</Icon>
                                        Exportar (.xlsx)
                                    </>
                                )}
                            </MDButton>
                        </Grid>

                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                {visao.ajuda}
                            </MDTypography>
                            {restritoEquipe && (
                                <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block", fontStyle: "italic" }}>
                                    Você visualiza a base das carteiras das suas equipes.
                                </MDTypography>
                            )}
                            <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block", opacity: 0.8 }}>
                                Só conta como acionamento o histórico de categoria Ocorrência (alterações, importações, suporte e sistema ficam fora).
                                Os filtros de Cliente e {visao.rotuloPessoa} valem só para a tela; a exportação respeita período e carteira.
                            </MDTypography>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>

            {loadingConsulta && !dados && (
                <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="center" alignItems="center" py={8}>
                        <CircularProgress size={36} color="info" />
                    </MDBox>
                </Grid>
            )}

            {atualizando && (
                <Grid item xs={12} sx={{ pt: "12px !important" }}>
                    <LinearProgress color="info" sx={{ borderRadius: 1 }} />
                </Grid>
            )}

            {semDados && (
                <Grid item xs={12}>
                    <Painel>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", textAlign: "center", py: 4 }}>
                            Nenhum acionamento encontrado no período selecionado.
                        </MDTypography>
                    </Painel>
                </Grid>
            )}

            {dados && blocos.length > 0 && indicadores && (
                <Grid item xs={12} sx={{ opacity: atualizando ? 0.5 : 1, transition: "opacity 0.2s", pointerEvents: atualizando ? "none" : "auto" }}>
                    <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            icone="assignment_turned_in"
                            cor={corMedida("fichas", darkMode)}
                            rotulo="Fichas Acionadas"
                            valor={formatarNumero(indicadores.fichas)}
                            detalhe="fichas distintas no trimestre"
                            variacao={variacaoCard(variacaoPercentual(indicadores.fichas, indicadoresAnterior?.fichas))}
                            rotuloVariacao={rotuloVariacao}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            icone="support_agent"
                            cor={corMedida("atendimentos", darkMode)}
                            rotulo="Atendimentos Realizados"
                            valor={formatarNumero(indicadores.atendimentos)}
                            detalhe="registros de histórico no trimestre"
                            variacao={variacaoCard(variacaoPercentual(indicadores.atendimentos, indicadoresAnterior?.atendimentos))}
                            rotuloVariacao={rotuloVariacao}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            icone="repeat"
                            cor={darkMode ? "#f06292" : "#e91e63"}
                            rotulo="Média por Ficha"
                            valor={formatarDecimal(indicadores.mediaPorFicha)}
                            detalhe="atendimentos por ficha acionada"
                            variacao={variacaoCard(variacaoPercentual(indicadores.mediaPorFicha, indicadoresAnterior?.mediaPorFicha))}
                            rotuloVariacao={rotuloVariacao}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            icone="donut_large"
                            cor={darkMode ? "#c3c2b7" : "#7b809a"}
                            rotulo="Maior Dedicação"
                            valor={clienteDestaque ? `${clienteDestaque} · ${formatarPercentualCurto(indicadores.principal.percentualTotal)}` : "-"}
                            detalhe="do esforço no trimestre"
                            variacao={clienteDestaque && indicadoresAnterior
                                ? variacaoPontos(indicadores.principal.percentualTotal, indicadoresAnterior.percentuaisPorCliente[clienteDestaque] || 0)
                                : null}
                            rotuloVariacao={rotuloVariacao}
                        />
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <Painel rotulo="Evolução dos Atendimentos">
                            <GraficoEvolucao meses={meses} fichasMes={indicadores.fichasMes} atendimentosMes={indicadores.atendimentosMes} />
                        </Painel>
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <Painel rotulo={`Distribuição dos Atendimentos por ${visao.rotuloPessoa}`}>
                            <GraficoDistribuicao itens={itensDistribuicao} cores={cores} selecionado={pessoaNome} rotuloCentro="Atendimentos" />
                        </Painel>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <Painel rotulo="Participação por Cliente (% das fichas acionadas)">
                            <GraficoEsforco
                                blocos={blocos}
                                cores={cores}
                                fichasPorCliente={fichasPorCliente}
                                selecionado={pessoaNome}
                                clienteDestaque={clienteNome}
                            />
                            <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.8 }}>
                                Cada barra soma 100% das fichas acionadas do cliente no trimestre, divididas entre os {visao.rotuloPessoas}.
                                O esforço de cada {visao.rotuloPessoa.toLowerCase()} por cliente está na matriz abaixo.
                            </MDTypography>
                        </Painel>
                    </Grid>

                    <Grid item xs={12}>
                        <Painel rotulo={`Matriz de Esforço · ${visao.rotuloPessoa} × Cliente`}>
                            <MatrizEsforco
                                blocos={blocos}
                                consolidado={consolidado}
                                totaisClientes={dados?.clientes}
                                selecionado={pessoaNome}
                                clienteDestaque={clienteNome}
                                rotuloPessoa={visao.rotuloPessoa}
                                onVerDetalhe={handleVerDetalhe}
                            />
                        </Painel>
                    </Grid>

                    <Grid item xs={12} lg={9} ref={detalheRef}>
                        <Painel
                            rotulo={`% do tempo por cliente · ${blocoDetalhe.nome}`}
                            acao={(
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDBox sx={{ px: 1.5, py: 0.25, borderRadius: 4, border: `1px solid ${darkMode ? "rgba(255,255,255,0.25)" : "#d2d6da"}` }}>
                                        <MDTypography variant="caption" color="text">
                                            Cada mês soma 100% das fichas acionadas pelo {visao.rotuloPessoa.toLowerCase()}.
                                        </MDTypography>
                                    </MDBox>
                                    <IconButton size="small" onClick={() => setDetalheAberto(!detalheAberto)} aria-label={detalheAberto ? "Recolher" : "Expandir"}>
                                        <Icon sx={{ color: darkMode ? "#fff" : "#344767" }}>{detalheAberto ? "expand_less" : "expand_more"}</Icon>
                                    </IconButton>
                                </MDBox>
                            )}
                        >
                            <Collapse in={detalheAberto}>
                                <TabelaPercentual bloco={blocoDetalhe} meses={meses} rotuloLinhas={visao.rotuloLinhas} />
                            </Collapse>
                        </Painel>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Painel rotulo={`Resumo por cliente · ${blocoDetalhe.nome}`}>
                            {resumoClientes.map(item => (
                                <MDBox
                                    key={item.cliente}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    py={0.75}
                                    sx={{ borderBottom: `1px solid ${darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0"}` }}
                                >
                                    <MDTypography variant="caption" color="text" fontWeight="bold">{item.cliente}</MDTypography>
                                    <MDBox textAlign="right">
                                        <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ display: "block" }}>
                                            {formatarPercentualCurto(item.percentual)}
                                        </MDTypography>
                                        <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.8 }}>
                                            {formatarNumero(item.fichas)} fichas · {formatarNumero(item.atendimentos)} atend.
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            ))}
                        </Painel>
                    </Grid>
                    </Grid>
                </Grid>
            )}

            <Grid item xs={12}>
                <Painel rotulo="Estrutura do arquivo exportado">
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        • <strong>Analise de % Tempo Negociador:</strong> Uma tabela por negociador com o percentual de dedicação a cada cliente mês a mês.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        • <strong>Base de Dados Negociador:</strong> Listagem analítica das fichas trabalhadas no trimestre por negociador.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        • <strong>Analise % Tempo Coordenador:</strong> Uma tabela por coordenador com o percentual de apoio por cliente, com base nas fichas acionadas pelos negociadores nas carteiras das suas equipes.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        • <strong>Base de Dados Coordenador:</strong> Listagem com a coluna <i>Responsavel</i> apontando o coordenador da equipe vinculada à carteira da ficha acionada.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        • Acionamento = histórico com tipo de ocorrência da categoria <strong>Ocorrência</strong>. Fichas <strong>devolvidas</strong> acionadas no período entram com saldo 0 e valor original preenchido. Fichas de honorários não entram.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
                        Clientes segmentados automaticamente em: <strong>GETNET</strong>, <strong>CIELO</strong>, <strong>PLUXEE</strong>, <strong>UMC</strong> e <strong>COBEXTRA</strong>.
                    </MDTypography>
                </Painel>
            </Grid>
        </Grid>
    );
}
